<template>
  <div>
    <button type="button" class="inline-flex items-center gap-2 text-[13px] text-muted bg-transparent border-0 cursor-pointer p-0 mb-6 transition-colors duration-150 hover:text-ink" @click="goBack">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
      Back to applicants
    </button>

    <skeleton-applicant v-if="isLoading"></skeleton-applicant>

    <div v-else-if="hasError" class="flex flex-col items-center py-[60px] px-5 text-muted">
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
      <p class="text-sm text-muted mt-4 mb-2">Could not load this applicant</p>
      <button type="button" class="text-sm font-semibold text-primary bg-transparent border-0 cursor-pointer hover:opacity-80" @click="retryLoad">Try again</button>
    </div>

    <template v-else-if="applicant">
      <!-- Header card — identity, actions, progress unified -->
      <div class="bg-white border border-black/6 rounded-[20px] p-6 mb-7 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div class="header-top flex items-start justify-between gap-5">
          <div class="header-identity flex items-start gap-5">
            <div class="shrink-0 w-16 h-16">
              <img v-if="applicant.picture" :src="'/static/images/clients/' + applicant.picture" :alt="applicantName" class="w-16 h-16 rounded-full object-cover">
              <div v-else class="w-16 h-16 rounded-full bg-border text-primary flex items-center justify-center font-bold text-[22px]">{{ applicant.firstName ? applicant.firstName.charAt(0) : '' }}</div>
            </div>
            <div>
              <div class="header-name-row flex items-center gap-2.5 mb-1">
                <h2 class="text-2xl font-bold text-ink tracking-[-0.03em] m-0">{{ applicantName }}</h2>
                <span v-if="applicant.type"
                  class="text-[11px] font-semibold uppercase tracking-[0.04em] px-2.5 py-[3px] rounded"
                  :class="{
                    'bg-border text-primary': applicant.type === 'talent',
                    'bg-[#e8f4fb] text-[#3AA6DD]': applicant.type === 'promise'
                  }">
                  {{ applicant.type === 'talent' ? 'Talent' : 'Promise' }}
                </span>
              </div>
              <p v-if="applicant && getEndorsingBody(applicant.endorsingBody)" class="text-sm text-muted m-0 mb-0.5">
                {{ getEndorsingBody(applicant.endorsingBody).title }}
              </p>
              <p v-if="applicant.shortDescription" class="text-sm text-[#645B66] mt-1.5 mb-0 leading-relaxed">{{ applicant.shortDescription }}</p>
            </div>
          </div>
          <div class="header-actions flex gap-2 shrink-0">
            <a v-if="user && user.isAdmin" href="#" @click.prevent="openEditModal(applicant)" class="btn-action inline-flex items-center gap-2 px-4 py-[7px] text-[13px] font-medium text-ink bg-white border border-black/10 rounded-full no-underline transition-all duration-200 cursor-pointer whitespace-nowrap hover:border-black/15 hover:shadow-[0_2px_6px_rgba(0,0,0,0.08)] hover:no-underline hover:text-ink">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              Edit
            </a>
            <a v-if="applicant.linkedin" :href="applicant.linkedin" target="_blank" class="btn-action inline-flex items-center gap-2 px-4 py-[7px] text-[13px] font-medium text-ink bg-white border border-black/10 rounded-full no-underline transition-all duration-200 cursor-pointer whitespace-nowrap hover:border-black/15 hover:shadow-[0_2px_6px_rgba(0,0,0,0.08)] hover:no-underline hover:text-ink">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
              LinkedIn
            </a>
            <a v-if="user && user.isAdmin && applicant.isActive" href="#"
              class="btn-action inline-flex items-center gap-2 px-4 py-[7px] text-[13px] font-medium bg-white border rounded-full no-underline transition-all duration-200 cursor-pointer whitespace-nowrap hover:border-black/15 hover:shadow-[0_2px_6px_rgba(0,0,0,0.08)] hover:no-underline"
              :class="confirmArchive ? 'text-[#e53935] border-[#e53935] hover:bg-[#fef2f2] hover:text-[#e53935]' : 'text-ink border-black/10 hover:text-ink'"
              @click.prevent.stop="archiveApplicant()">
              <template v-if="confirmArchive">Archive?</template>
              <template v-else>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                Archive
              </template>
            </a>
          </div>
        </div>

        <!-- Progress integrated into header -->
        <div class="mt-5 pt-5 border-t border-black/6">
          <div class="flex items-center justify-between mb-2.5">
            <span class="text-[13px] text-muted font-medium">{{ getCompletedCount() }} of {{ applicant.requirements ? applicant.requirements.length : 0 }} complete</span>
            <span class="text-[13px] text-ink font-semibold">{{ getCompletedPercentage(applicant) }}%</span>
          </div>
          <div class="h-1.5 bg-border rounded-[3px] overflow-hidden">
            <div class="h-full bg-primary rounded-[3px] transition-[width] duration-300 ease-in-out" :style="{ width: getCompletedPercentage(applicant) + '%' }"></div>
          </div>
        </div>
        <case-pulse :applicant="applicant"></case-pulse>
      </div>

      <div class="applicant-body grid items-start">
        <div class="min-w-0">
          <!-- Mandatory criteria -->
          <div v-if="getMandatoryRequirements.length" class="mb-6">
            <button class="section-header-clickable flex items-center gap-2.5 mb-3 cursor-pointer select-none rounded-md px-1.5 py-1 -mx-1.5 transition-colors duration-150 hover:bg-canvas w-full bg-transparent border-0 text-left" @click="toggleSection('mandatory')">
              <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Mandatory criteria</span>
              <span class="bg-border text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{{ getCompleteMandatoryRequirements.length }}/{{ getMandatoryRequirements.length }}</span>
              <svg class="section-chevron ml-auto text-placeholder shrink-0 transition-transform duration-200" :class="{ 'section-chevron--collapsed': isSectionCollapsed('mandatory') }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </button>
            <transition name="section-fade">
            <requirements-table
              v-if="!isSectionCollapsed('mandatory')"
              :items="getMandatoryRequirements"
              :applicant="applicant"
              :type="'mandatory'"
              @openRequirementModal="openRequirementModal"
              @deleteRequirement="deleteRequirement"
              @statusChanged="onStatusChanged"
              @update="update">
            </requirements-table>
            </transition>
          </div>

          <!-- Optional criteria -->
          <div v-if="getOptionalRequirements.length" class="mb-6">
            <button class="section-header-clickable flex items-center gap-2.5 mb-3 cursor-pointer select-none rounded-md px-1.5 py-1 -mx-1.5 transition-colors duration-150 hover:bg-canvas w-full bg-transparent border-0 text-left" @click="toggleSection('optional')">
              <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Optional criteria</span>
              <span class="bg-border text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{{ getCompleteOptionalRequirements.length }}/{{ getOptionalRequirements.length }}</span>
              <svg class="section-chevron ml-auto text-placeholder shrink-0 transition-transform duration-200" :class="{ 'section-chevron--collapsed': isSectionCollapsed('optional') }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </button>
            <transition name="section-fade">
            <requirements-table
              v-if="!isSectionCollapsed('optional')"
              :items="getOptionalRequirements"
              :applicant="applicant"
              :type="'optional'"
              @openRequirementModal="openRequirementModal"
              @deleteRequirement="deleteRequirement"
              @statusChanged="onStatusChanged"
              @update="update">
            </requirements-table>
            </transition>
          </div>

          <!-- Letters of recommendation -->
          <div v-if="getLettersOfRecommendationRequirements.length" class="mb-6">
            <button class="section-header-clickable flex items-center gap-2.5 mb-3 cursor-pointer select-none rounded-md px-1.5 py-1 -mx-1.5 transition-colors duration-150 hover:bg-canvas w-full bg-transparent border-0 text-left" @click="toggleSection('letterOfRecommendation')">
              <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Letters of recommendation</span>
              <span class="bg-border text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{{ getCompleteLettersOfRecommendationRequirements.length }}/{{ getLettersOfRecommendationRequirements.length }}</span>
              <svg class="section-chevron ml-auto text-placeholder shrink-0 transition-transform duration-200" :class="{ 'section-chevron--collapsed': isSectionCollapsed('letterOfRecommendation') }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </button>
            <transition name="section-fade">
            <requirements-table
              v-if="!isSectionCollapsed('letterOfRecommendation')"
              :items="getLettersOfRecommendationRequirements"
              :applicant="applicant"
              :type="'letterOfRecommendation'"
              @openRequirementModal="openRequirementModal"
              @deleteRequirement="deleteRequirement"
              @statusChanged="onStatusChanged"
              @update="update">
            </requirements-table>
            </transition>
          </div>

          <!-- Other -->
          <div v-if="getOtherRequirements.length" class="mb-6">
            <button class="section-header-clickable flex items-center gap-2.5 mb-3 cursor-pointer select-none rounded-md px-1.5 py-1 -mx-1.5 transition-colors duration-150 hover:bg-canvas w-full bg-transparent border-0 text-left" @click="toggleSection('other')">
              <span class="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">Other</span>
              <span class="bg-border text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{{ getCompleteOtherRequirements.length }}/{{ getOtherRequirements.length }}</span>
              <svg class="section-chevron ml-auto text-placeholder shrink-0 transition-transform duration-200" :class="{ 'section-chevron--collapsed': isSectionCollapsed('other') }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </button>
            <transition name="section-fade">
            <requirements-table
              v-if="!isSectionCollapsed('other')"
              :items="getOtherRequirements"
              :applicant="applicant"
              :type="'other'"
              @openRequirementModal="openRequirementModal"
              @deleteRequirement="deleteRequirement"
              @statusChanged="onStatusChanged"
              @update="update">
            </requirements-table>
            </transition>
          </div>

          <div v-if="!applicant.requirements || applicant.requirements.length === 0" class="flex flex-col items-center py-10 text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>
            <p class="text-sm text-muted mt-3 mb-0">No requirements yet</p>
          </div>

          <div class="pt-1 pb-6">
            <button type="button" @click="openRequirementModal()" class="add-req-btn flex items-center justify-center w-full gap-2 px-4 py-[11px] text-sm font-medium text-white bg-primary border border-dashed border-primary rounded-[10px] transition-all duration-200 cursor-pointer whitespace-nowrap hover:opacity-85">
              + Add requirement
            </button>
          </div>
        </div>

        <div class="applicant-sidebar sticky top-9 max-h-[calc(100vh-72px)] overflow-y-auto pl-7">
          <!-- Key dates -->
          <div class="mb-6">
            <deadline-tracker
              :deadlines="applicant.keyDates || []"
              :applicant="applicant"
              @addDeadline="openDeadlineModal()"
              @editDeadline="openDeadlineEditModal"
              @deleteDeadline="deleteDeadline"
              @toggleDeadline="toggleDeadline">
            </deadline-tracker>
          </div>

          <!-- Communication checklist (admin only) -->
          <div v-if="user && user.isAdmin" class="mb-6">
            <contact-checklist
              :items="applicant.contactChecklist || []"
              @toggleItem="toggleChecklistItem"
              @addItem="addChecklistItem"
              @deleteItem="deleteChecklistItem">
            </contact-checklist>
          </div>

          <!-- Activity timeline -->
          <div class="mb-6">
            <activity-timeline :applicant-id="applicant.id"></activity-timeline>
          </div>
        </div>
      </div>
      <!-- Sticky note bar -->
      <div class="note-bar sticky bottom-0 z-10 bg-white border-t border-black/8 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] -mx-10 mt-6 px-10 py-4">
        <div class="flex items-start gap-3 max-w-full">
          <img v-if="userPhoto" :src="userPhoto" :alt="user.firstName" class="note-avatar shrink-0 w-8 h-8 rounded-full object-cover mt-1">
          <div v-else class="note-avatar shrink-0 w-8 h-8 rounded-full bg-border text-primary flex items-center justify-center font-bold text-[13px] mt-1">{{ user && (user.firstName || user.name) ? (user.firstName || user.name).charAt(0) : '?' }}</div>
          <div class="flex-1 relative">
            <textarea
              v-model="noteText"
              ref="noteTextarea"
              placeholder="Add a note..."
              class="w-full border border-black/12 rounded-xl py-2.5 pr-10 pl-3.5 text-sm font-[inherit] text-ink bg-canvas resize-none min-h-[42px] leading-relaxed outline-none box-border transition-[border-color,background,box-shadow] duration-150 placeholder:text-placeholder focus:border-primary focus:bg-white focus:shadow-[0_0_0_3px_rgba(124,91,234,0.1)]"
              rows="1"
              @input="autoResize"
              @keydown.enter.meta="addNote"
              @keydown.enter.ctrl="addNote"
            ></textarea>
            <button
              v-if="noteText.trim()"
              class="absolute right-2 bottom-2 bg-primary border-none rounded-lg w-7 h-7 flex items-center justify-center text-white cursor-pointer transition-opacity duration-150 hover:opacity-85"
              @click="addNote">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            </button>
          </div>
        </div>
      </div>
    </template>

    <applicant-add ref="modal" :item="applicant" @update="update"></applicant-add>
    <requirement-add ref="requirementModal" :applicant="applicant" :item="editingRequirement" :requirementIndex="requirementIndex" @update="update"></requirement-add>
    <deadline-add-modal ref="deadlineModal" :item="deadlineItem" :edit-index="deadlineEditIndex" @save="saveDeadline"></deadline-add-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getCompletedPercentage, getAssignedToText } from '@/services/applicant-helpers'
import ApplicantAdd from './Includes/ApplicantAddModal'
import RequirementModal from './Applicant/RequirementModal'
import RequirementsTable from './Includes/RequirementsTable'
import ActivityTimeline from './Applicant/ActivityTimeline'
import CasePulse from './Applicant/CasePulse'
import DeadlineTracker from './Applicant/DeadlineTracker'
import DeadlineAddModal from './Applicant/DeadlineAddModal'
import { PHOTOS } from '@/services/photos'
import ContactChecklist from './Applicant/ContactChecklist'
import SkeletonApplicant from '../UIComponents/SkeletonApplicant'

export default {
  name: 'applicant',
  props: ['id'],
  components: {
    applicantAdd: ApplicantAdd,
    requirementAdd: RequirementModal,
    requirementsTable: RequirementsTable,
    activityTimeline: ActivityTimeline,
    deadlineTracker: DeadlineTracker,
    deadlineAddModal: DeadlineAddModal,
    casePulse: CasePulse,
    contactChecklist: ContactChecklist,
    skeletonApplicant: SkeletonApplicant
  },
  data () {
    return {
      isLoading: null,
      hasError: false,
      confirmArchive: false,
      isArchiving: false,
      editingRequirement: null,
      requirementIndex: null,
      deadlineItem: null,
      deadlineEditIndex: -1,
      unsubApplicant: null,
      sectionOverrides: {},
      noteText: ''
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      applicant: state => state.applicants.applicant,
      applicationTypes: state => state.auth.applicationTypes,
      requirementTypes: state => state.auth.requirementTypes,
      bodies: state => state.managers.bodies,
      managers: state => state.managers.managers,
      applicationStatuses: state => state.components.applicationStatuses
    }),
    userPhoto () {
      if (!this.user) return null
      const name = this.user.firstName || this.user.name || ''
      if (!name) return null
      // Try exact match first, then first word of name (for "Renat Galyamov" → "renat")
      return PHOTOS[name.toLowerCase()] || PHOTOS[name.split(' ')[0].toLowerCase()] || this.user.photoUrl || null
    },
    applicantName () {
      if (!this.applicant) return ''
      return this.applicant.firstName + ' ' + this.applicant.lastName
    },
    getMandatoryRequirements () {
      if (!this.applicant || !this.applicant.requirements) {
        return []
      }
      return this.applicant.requirements.filter(requirement => requirement.category === 'mandatoryCriteria')
    },
    getCompleteMandatoryRequirements () {
      if (!this.applicant || !this.applicant.requirements) {
        return []
      }
      return this.applicant.requirements.filter(requirement => requirement.category === 'mandatoryCriteria' && (requirement.status === 'complete' || requirement.status === 'submitted'))
    },
    getOptionalRequirements () {
      if (!this.applicant || !this.applicant.requirements) {
        return []
      }
      return this.applicant.requirements.filter(requirement => requirement.category === 'optionalCriteria')
    },
    getCompleteOptionalRequirements () {
      if (!this.applicant || !this.applicant.requirements) {
        return []
      }
      return this.applicant.requirements.filter(requirement => requirement.category === 'optionalCriteria' && (requirement.status === 'complete' || requirement.status === 'submitted'))
    },
    getLettersOfRecommendationRequirements () {
      if (!this.applicant || !this.applicant.requirements) {
        return []
      }
      return this.applicant.requirements.filter(requirement => requirement.category === 'letterOfRecommendation')
    },
    getCompleteLettersOfRecommendationRequirements () {
      if (!this.applicant || !this.applicant.requirements) {
        return []
      }
      return this.applicant.requirements.filter(requirement => requirement.category === 'letterOfRecommendation' && (requirement.status === 'complete' || requirement.status === 'submitted'))
    },
    getOtherRequirements () {
      if (!this.applicant || !this.applicant.requirements) {
        return []
      }
      return this.applicant.requirements.filter(requirement => requirement.category === 'personalStatement' || requirement.category === 'cv' || requirement.category === 'other')
    },
    getCompleteOtherRequirements () {
      if (!this.applicant || !this.applicant.requirements) {
        return []
      }
      return this.applicant.requirements.filter(requirement => (requirement.category === 'personalStatement' || requirement.category === 'cv' || requirement.category === 'other') && (requirement.status === 'complete' || requirement.status === 'submitted'))
    }
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push({ name: 'admin-applicants' })
      }
    },
    retryLoad () {
      this.hasError = false
      this.isLoading = true
      const id = this.id || this.$route.params.id
      this.$store.dispatch('GET_APPLICANT', { id }).then((unsub) => {
        this.unsubApplicant = unsub
        this.isLoading = false
        this.seedChecklist()
      }).catch(() => {
        this.isLoading = false
        this.hasError = true
      })
    },
    getCompletedPercentage (applicant) {
      return getCompletedPercentage(applicant)
    },
    getCompletedCount () {
      let completed = 0
      if (this.applicant && this.applicant.requirements) {
        this.applicant.requirements.forEach(requirement => {
          if (requirement.status && (requirement.status === 'complete' || requirement.status === 'submitted' || requirement.status === 'sentToApplicant')) {
            completed++
          }
        })
      }
      return completed
    },
    getAssignedTo (requirement) {
      return getAssignedToText(requirement, this.managers)
    },
    getStatus (status) {
      const statusItem = this.applicationStatuses.filter((item) => {
        return item.value === status
      })[0]
      return statusItem ? statusItem.text : '-'
    },
    openRequirementModal (requirement) {
      this.editingRequirement = requirement || null
      this.requirementIndex = requirement
        ? this.applicant.requirements.findIndex((item) => item.createdAt === requirement.createdAt)
        : -1

      this.$nextTick(() => {
        this.$refs.requirementModal.showModal()
      })
    },
    closeRequirementModal () {
      this.$refs.requirementModal.hideModal()
    },
    getEndorsingType (type) {
      const item = this.applicationTypes.find((item) => {
        return item.value === type
      })
      return item ? item.text : ''
    },
    getEndorsingBody (type) {
      const item = this.bodies.find((item) => {
        return item.slug === type
      })
      return item
    },
    getCategoryTitle (category) {
      const item = this.requirementTypes.find((item) => {
        return item.value === category
      })
      return item ? item.text : ''
    },
    getApplicationTitle (type) {
      const item = this.applicationTypes.find((item) => {
        return item.value === type
      })
      return item ? item.text : ''
    },
    openEditModal () {
      this.editApplicant = null
      this.$refs.modal.showModal()
    },
    update () {
      this.isLoading = true
      const id = this.id || this.$route.params.id
      // Unsubscribe previous listener before creating a new one
      if (this.unsubApplicant) {
        this.unsubApplicant()
        this.unsubApplicant = null
      }
      this.$store.dispatch('GET_APPLICANT', { id }).then((unsub) => {
        if (this._isDestroyed) {
          if (unsub) unsub()
          return
        }
        this.unsubApplicant = unsub
        this.isLoading = false
      }).catch(() => {
        if (!this._isDestroyed) {
          this.isLoading = false
          this.$store.dispatch('SHOW_TOAST', { message: 'Failed to refresh applicant', type: 'error' })
        }
      })
    },
    deleteRequirement (createdAt) {
      const index = this.applicant.requirements.findIndex((item) => {
        return item.createdAt === createdAt
      })

      const requirements = this.applicant.requirements.slice(0, index).concat(this.applicant.requirements.slice(index + 1, this.applicant.requirements.length))
      this.$store.dispatch('UPDATE_APPLICANT_REQUIREMENTS', { applicantId: this.applicant.id, requirements: requirements }).then(() => {
        this.logActivity('requirement', 'removed a requirement')
        this.$store.dispatch('SHOW_TOAST', { message: 'Requirement removed' })
      })
    },
    // Deadline methods
    openDeadlineModal () {
      this.deadlineItem = null
      this.deadlineEditIndex = -1
      this.$nextTick(() => {
        this.$refs.deadlineModal.showModal()
      })
    },
    openDeadlineEditModal (index) {
      const deadlines = this.applicant.keyDates || []
      this.deadlineItem = deadlines[index] || null
      this.deadlineEditIndex = index
      this.$nextTick(() => {
        this.$refs.deadlineModal.showModal()
      })
    },
    saveDeadline (payload) {
      const keyDates = [...(this.applicant.keyDates || [])]
      if (payload.index >= 0) {
        keyDates[payload.index] = payload.deadline
      } else {
        keyDates.push(payload.deadline)
      }
      this.$store.dispatch('UPDATE_APPLICANT_KEY_DATES', {
        applicantId: this.applicant.id,
        keyDates: keyDates
      }).then(() => {
        this.update()
        const action = payload.index >= 0 ? 'updated' : 'added'
        this.logActivity('deadline', action + ' key date: ' + payload.deadline.label)
        this.$store.dispatch('SHOW_TOAST', { message: 'Key date ' + action })
      })
    },
    deleteDeadline (index) {
      const keyDates = [...(this.applicant.keyDates || [])]
      const removed = keyDates.splice(index, 1)
      this.$store.dispatch('UPDATE_APPLICANT_KEY_DATES', {
        applicantId: this.applicant.id,
        keyDates: keyDates
      }).then(() => {
        this.update()
        if (removed.length) {
          this.logActivity('deadline', 'removed key date: ' + removed[0].label)
        }
      })
    },
    toggleDeadline (index) {
      const keyDates = [...(this.applicant.keyDates || [])]
      keyDates[index] = { ...keyDates[index], completed: !keyDates[index].completed }
      this.$store.dispatch('UPDATE_APPLICANT_KEY_DATES', {
        applicantId: this.applicant.id,
        keyDates: keyDates
      }).then(() => {
        this.update()
        const status = keyDates[index].completed ? 'completed' : 'uncompleted'
        this.logActivity('deadline', status + ' key date: ' + keyDates[index].label)
      })
    },
    // Checklist methods
    seedChecklist () {
      if (!this.applicant || !this.user || !this.user.isAdmin) return
      if (this.applicant.contactChecklist && this.applicant.contactChecklist.length > 0) return
      const defaults = [
        { label: 'Shared case plan', checked: false, predefined: true },
        { label: 'Added first evidence', checked: false, predefined: true },
        { label: 'Sent first update', checked: false, predefined: true },
        { label: 'Checked all evidences', checked: false, predefined: true },
        { label: 'Synced LinkedIn', checked: false, predefined: true }
      ]
      this.$store.dispatch('UPDATE_APPLICANT_CHECKLIST', {
        applicantId: this.applicant.id,
        contactChecklist: defaults
      })
    },
    toggleChecklistItem (index) {
      const checklist = [...(this.applicant.contactChecklist || [])]
      checklist[index] = { ...checklist[index], checked: !checklist[index].checked }
      this.$store.dispatch('UPDATE_APPLICANT_CHECKLIST', {
        applicantId: this.applicant.id,
        contactChecklist: checklist
      }).then(() => {
        const status = checklist[index].checked ? 'checked' : 'unchecked'
        this.logActivity('communication', status + ': ' + checklist[index].label)
      })
    },
    addChecklistItem (label) {
      const checklist = [...(this.applicant.contactChecklist || [])]
      checklist.push({ label: label, checked: false, predefined: false })
      this.$store.dispatch('UPDATE_APPLICANT_CHECKLIST', {
        applicantId: this.applicant.id,
        contactChecklist: checklist
      }).then(() => {
        this.logActivity('communication', 'added checklist item: ' + label)
      })
    },
    deleteChecklistItem (index) {
      const checklist = [...(this.applicant.contactChecklist || [])]
      const removed = checklist.splice(index, 1)
      this.$store.dispatch('UPDATE_APPLICANT_CHECKLIST', {
        applicantId: this.applicant.id,
        contactChecklist: checklist
      }).then(() => {
        if (removed.length) {
          this.logActivity('communication', 'removed checklist item: ' + removed[0].label)
        }
      })
    },
    // Activity logging helper
    logActivity (type, message, details, changes) {
      if (!this.applicant || !this.applicant.id) return
      const userName = this.user ? (this.user.firstName || this.user.username) : 'Unknown'
      const payload = {
        applicantId: this.applicant.id,
        type: type,
        message: message,
        details: details || null,
        user: userName
      }
      if (changes && changes.length) {
        payload.changes = changes
      }
      this.$store.dispatch('CREATE_ACTIVITY', payload).catch(() => {})
    },
    isSectionAllComplete (key) {
      const map = {
        mandatory: [this.getMandatoryRequirements, this.getCompleteMandatoryRequirements],
        optional: [this.getOptionalRequirements, this.getCompleteOptionalRequirements],
        letterOfRecommendation: [this.getLettersOfRecommendationRequirements, this.getCompleteLettersOfRecommendationRequirements],
        other: [this.getOtherRequirements, this.getCompleteOtherRequirements]
      }
      const pair = map[key]
      if (!pair) return false
      return pair[0].length > 0 && pair[0].length === pair[1].length
    },
    isSectionCollapsed (key) {
      if (this.sectionOverrides[key] !== undefined) {
        return this.sectionOverrides[key]
      }
      return this.isSectionAllComplete(key)
    },
    toggleSection (key) {
      const val = !this.isSectionCollapsed(key)
      this.$set(this.sectionOverrides, key, val)
      try {
        const stored = JSON.parse(localStorage.getItem('sectionCollapse') || '{}')
        stored[key] = val
        localStorage.setItem('sectionCollapse', JSON.stringify(stored))
      } catch (e) {}
    },
    addNote () {
      if (!this.noteText.trim()) return
      this.logActivity('note', 'added a note', this.noteText.trim())
      this.$store.dispatch('SHOW_TOAST', { message: 'Note added' })
      this.noteText = ''
      if (this.$refs.noteTextarea) {
        this.$refs.noteTextarea.style.height = 'auto'
      }
    },
    autoResize (event) {
      const el = event.target
      if (el) {
        el.style.height = 'auto'
        el.style.height = el.scrollHeight + 'px'
      }
    },
    onStatusChanged ({ requirement, from, to }) {
      this.logActivity('requirement', 'changed requirement status', requirement.title, [
        { field: 'Status', from: this.getStatus(from), to: this.getStatus(to) }
      ])
    },
    archiveApplicant () {
      if (this.isArchiving) return
      if (this.confirmArchive) {
        this.confirmArchive = false
        this.isArchiving = true
        const data = { ...this.applicant, isActive: false }
        this.$store.dispatch('UPDATE_APPLICANT', data).then(() => {
          this.isArchiving = false
          this.logActivity('status', 'archived the applicant')
          this.$store.dispatch('SHOW_TOAST', { message: 'Applicant archived' })
          this.$router.push({ name: 'admin-applicants' })
        }).catch(() => {
          this.isArchiving = false
          this.$store.dispatch('SHOW_TOAST', { message: 'Failed to archive applicant', type: 'error' })
        })
      } else {
        this.confirmArchive = true
      }
    }
  },
  mounted () {
    this._resetConfirmArchive = () => { this.confirmArchive = false }
    document.addEventListener('click', this._resetConfirmArchive)
  },
  created () {
    this._isDestroyed = false
    try {
      const stored = JSON.parse(localStorage.getItem('sectionCollapse') || '{}')
      this.sectionOverrides = { ...stored }
    } catch (e) {}
    this.isLoading = true
    const id = this.id || this.$route.params.id
    this.$store.dispatch('GET_APPLICANT', { id }).then((unsub) => {
      if (this._isDestroyed) {
        if (unsub) unsub()
        return
      }
      this.unsubApplicant = unsub
      this.isLoading = false
      this.seedChecklist()

      if (this.user && this.user.isManager && !this.user.isAdmin) {
        const assignedTo = this.applicant && this.applicant.availableTo
        if (!assignedTo || !assignedTo.includes(this.user.firstName.toLowerCase())) {
          this.$router.push({ name: 'admin-applicants' })
        }
      }
    }).catch((error) => {
      this.isLoading = false
      this.hasError = true
      console.error('Failed to load applicant:', error)
      if (error.code === 'permission-denied' || error.code === 'PERMISSION_DENIED') {
        this.$router.push({ name: 'admin-applicants' })
      }
    })

    this.$store.dispatch('GET_BODIES').catch((error) => {
      console.error('Failed to load bodies:', error)
    })

    if (this.user && !this.user.isAdmin && !this.user.isManager) {
      this.$router.push({ name: 'components' })
    }
  },
  beforeDestroy () {
    this._isDestroyed = true
    if (this._resetConfirmArchive) {
      document.removeEventListener('click', this._resetConfirmArchive)
    }
    if (this.unsubApplicant) {
      this.unsubApplicant()
      this.unsubApplicant = null
    }
    this.$store.commit('SET_APPLICANT', null)
  }
}
</script>

<style scoped>
/* Two-column grid layout */
.applicant-body {
  grid-template-columns: 1fr 340px;
}

/* Section chevron collapse rotation */
.section-chevron--collapsed {
  transform: rotate(-90deg);
}

/* Section fade transition */
.section-fade-enter-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.section-fade-leave-active {
  transition: opacity 0.1s ease;
}

.section-fade-enter {
  opacity: 0;
  transform: translateY(-4px);
}

.section-fade-leave-to {
  opacity: 0;
}

/* Adjacent sibling: requirements-table after clickable header */
.section-header-clickable + .requirements-table {
  margin-top: 0;
}

/* Responsive: tablet breakpoint — stack grid to single column */
@media (max-width: 1200px) {
  .applicant-body {
    grid-template-columns: 1fr;
  }

  .applicant-sidebar {
    position: static;
    max-height: none;
    overflow-y: visible;
    border-left: none;
    padding-left: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    padding-top: 28px;
    margin-top: 4px;
  }
}

/* Responsive: mobile breakpoint — many overrides */
@media (max-width: 768px) {
  .header-top {
    flex-direction: column;
    gap: 16px;
  }

  .header-identity {
    gap: 14px;
  }

  .header-identity > div:first-child {
    width: 48px;
    height: 48px;
  }

  .header-identity > div:first-child img {
    width: 48px;
    height: 48px;
  }

  .header-identity > div:first-child > div {
    width: 48px;
    height: 48px;
    font-size: 18px;
  }

  .header-name-row {
    flex-wrap: wrap;
    gap: 8px;
  }

  .header-name-row h2 {
    font-size: 20px;
  }

  .header-actions {
    width: 100%;
  }

  .header-actions > a {
    flex: 1;
    justify-content: center;
  }

  .section-header-clickable {
    margin-bottom: 10px;
  }

  .note-bar {
    margin-left: -16px;
    margin-right: -16px;
    padding: 12px 16px;
  }

  .note-bar > div {
    gap: 8px;
  }

  .note-avatar {
    display: none;
  }
}
</style>
